import { useEffect, useRef } from "react";

import type { AdminLabRow } from "../../services/labService";

interface AdminLabActionsMenuProps {
  lab: AdminLabRow;
  isBusy: boolean;
  onManageAuditor: (lab: AdminLabRow) => void;
  onJoin: (labId: string) => Promise<void>;
  onLeave: (labId: string) => Promise<void>;
  onRemove: (lab: AdminLabRow) => void;
}

interface MenuActionContentProps {
  label: string;
  helperText?: string | null;
}

const menuItemContentClass =
  "!grid-flow-row !grid-cols-none !items-start !gap-0";

const MenuActionContent = ({ label, helperText }: MenuActionContentProps) => (
  <>
    <span>{label}</span>
    {helperText && (
      <span className="mt-1 text-xs leading-tight text-base-content/60">
        {helperText}
      </span>
    )}
  </>
);

const getAuditorManagementUnavailableReason = (lab: AdminLabRow): string | null => {
  const certification = lab.current_certification;

  if (!certification) {
    return "Start a certification before assigning an auditor.";
  }

  switch (certification.workflow_status) {
    case "in_progress":
      return "Available once the certification is submitted for review.";
    case "certified":
      return "This certification has already been awarded.";
    case "expired":
      return "This certification has expired.";
    default:
      return null;
  }
};

export const AdminLabActionsMenu = ({
  lab,
  isBusy,
  onManageAuditor,
  onJoin,
  onLeave,
  onRemove,
}: AdminLabActionsMenuProps) => {
  const menuRef = useRef<HTMLDetailsElement>(null);
  const joinHelperText =
    lab.join_disabled_reason === "already_in_other_lab"
      ? "Already in another lab"
      : lab.join_disabled_reason === "lab_full"
        ? "Lab is full"
        : null;
  const workflowStatus = lab.current_certification?.workflow_status;
  const canManageAuditor =
    workflowStatus === "ready_to_assign" || workflowStatus === "assigned";
  const auditorManagementUnavailableReason =
    getAuditorManagementUnavailableReason(lab);
  const manageAuditorLabel = lab.current_certification?.auditor
    ? "Change auditor"
    : lab.current_certification?.pending_auditor_invite
      ? "Manage auditor"
      : "Assign auditor";

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const menu = menuRef.current;
      if (!menu?.open) {
        return;
      }
      if (event.target instanceof Node && !menu.contains(event.target)) {
        menu.open = false;
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  return (
    <details ref={menuRef} className="dropdown dropdown-end">
      <summary className="btn btn-ghost btn-sm m-0">More</summary>
      <ul className="menu dropdown-content z-[1] mt-2 w-52 rounded-box border border-[#eaddc9] bg-base-100 p-2 shadow">
        {lab.is_member ? (
          <li className={!lab.can_leave || isBusy ? "menu-disabled" : undefined}>
            <button
              type="button"
              className={menuItemContentClass}
              onClick={() => {
                void onLeave(lab.id);
              }}
              disabled={!lab.can_leave || isBusy}
            >
              <MenuActionContent label="Leave" />
            </button>
          </li>
        ) : (
          <li className={!lab.can_join || isBusy ? "menu-disabled" : undefined}>
            <button
              type="button"
              className={menuItemContentClass}
              onClick={() => {
                void onJoin(lab.id);
              }}
              disabled={!lab.can_join || isBusy}
            >
              <MenuActionContent label="Join" helperText={joinHelperText} />
            </button>
          </li>
        )}
        {canManageAuditor ? (
          <li className={isBusy ? "menu-disabled" : undefined}>
            <button
              type="button"
              className={menuItemContentClass}
              onClick={(event) => {
                event.preventDefault();
                menuRef.current?.removeAttribute("open");
                onManageAuditor(lab);
              }}
              disabled={isBusy}
            >
              <MenuActionContent label={manageAuditorLabel} />
            </button>
          </li>
        ) : (
          <li className="menu-disabled">
            <span className={menuItemContentClass} aria-disabled="true">
              <MenuActionContent
                label={manageAuditorLabel}
                helperText={auditorManagementUnavailableReason}
              />
            </span>
          </li>
        )}
        {lab.can_remove && (
          <>
            <li className="my-1 border-t border-base-200" />
            <li className={isBusy ? "menu-disabled" : undefined}>
              <button
                type="button"
                className={`${menuItemContentClass} text-error`}
                onClick={() => onRemove(lab)}
                disabled={isBusy}
              >
                <MenuActionContent label="Remove" />
              </button>
            </li>
          </>
        )}
      </ul>
    </details>
  );
};
